/**
 * Speech Queue Store
 *
 * Manages a priority queue of speech items for the avatar's TTS system.
 * Uses Zustand for reactive state that React components can subscribe to.
 *
 * KEY CONCEPTS:
 * - Priority Queue: High-priority items (warnings) are spoken before normal items
 * - Message Correlation: Each speech item has a unique ID for tracking success/failure
 * - Retry Logic: Failed items retry up to MAX_SPEECH_RETRIES times
 * - Timeout Handling: Items that take too long are automatically marked as failed
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import {
  SPEAK_TIMEOUT_MS,
  RETRY_DELAY_MS,
  MAX_SPEECH_RETRIES,
} from '@really-assess/shared/constants'
import { generateMessageId } from '@really-assess/shared/utils'
import type { SpeechItem, PendingMessage, SpeechPriority } from '@really-assess/shared/types'
import { debug } from '../utils/debug'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Function signature for sending speech to the avatar */
export type SpeakFunction = (item: SpeechItem) => void

/**
 * Priority Queue Implementation
 *
 * Maintains two separate arrays: one for high-priority items (warnings),
 * one for normal items. High-priority items are always dequeued first.
 *
 * Example: If queue has [normal1, normal2] and you add a warning,
 * the next dequeue() returns the warning, not normal1.
 */
class SpeechPriorityQueue {
  private highPriorityItems: SpeechItem[] = []
  private normalPriorityItems: SpeechItem[] = []

  /** Add item to appropriate priority bucket */
  enqueue(item: SpeechItem): void {
    if (item.priority === 'high') {
      this.highPriorityItems.push(item)
    } else {
      this.normalPriorityItems.push(item)
    }
  }

  /** Remove and return highest priority item (high before normal, FIFO within each) */
  dequeue(): SpeechItem | undefined {
    if (this.highPriorityItems.length > 0) {
      return this.highPriorityItems.shift()
    }
    return this.normalPriorityItems.shift()
  }

  /** Preview next item without removing it */
  peek(): SpeechItem | undefined {
    if (this.highPriorityItems.length > 0) {
      return this.highPriorityItems[0]
    }
    return this.normalPriorityItems[0]
  }

  /** Remove all items from queue */
  clear(): void {
    this.highPriorityItems = []
    this.normalPriorityItems = []
  }

  /** Total items across both priority levels */
  get length(): number {
    return this.highPriorityItems.length + this.normalPriorityItems.length
  }

  /** Get all items for debugging (high priority first, then normal) */
  toArray(): SpeechItem[] {
    return [...this.highPriorityItems, ...this.normalPriorityItems]
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Promise Resolution Tracking
// ─────────────────────────────────────────────────────────────────────────────

/** Tracks promise resolvers for awaiting speech completion */
interface SpeechPromiseResolver {
  resolve: () => void
  reject: (reason: string) => void
}

/** Map of messageId -> promise resolver for waitForSpeech */
const speechPromiseResolvers = new Map<string, SpeechPromiseResolver>()

// ─────────────────────────────────────────────────────────────────────────────
// Store State Interface
// ─────────────────────────────────────────────────────────────────────────────

interface SpeechQueueState {
  // State
  queue: SpeechPriorityQueue
  currentItem: SpeechItem | null       // Item currently being spoken
  isSpeaking: boolean                   // True while audio is playing
  pendingMessages: Map<string, PendingMessage>  // Tracks items awaiting completion

  // Speak function injected by Avatar component
  speakFn: SpeakFunction | null

  // Public actions
  enqueue: (text: string, priority?: SpeechPriority) => string
  markComplete: (messageId: string) => void
  markFailed: (messageId: string, reason: string) => void
  clearQueue: () => void
  processNext: () => void

  // Configuration
  setSpeakFunction: (fn: SpeakFunction | null) => void

  // Internal helpers (prefixed with underscore)
  _scheduleRetry: (item: SpeechItem) => void
  _cleanup: () => void
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Implementation
// ─────────────────────────────────────────────────────────────────────────────

export const useSpeechQueueStore = create<SpeechQueueState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    queue: new SpeechPriorityQueue(),
    currentItem: null,
    isSpeaking: false,
    pendingMessages: new Map(),
    speakFn: null,

    /**
     * Inject the speak function from Avatar component.
     * This decouples the queue logic from the actual TTS implementation.
     */
    setSpeakFunction: (fn) => {
      set({ speakFn: fn })
    },

    /**
     * Add text to the speech queue.
     * Returns a unique messageId for tracking this speech item.
     */
    enqueue: (text, priority = 'normal') => {
      const messageId = generateMessageId()

      const item: SpeechItem = {
        text,
        priority,
        messageId,
        retryCount: 0,
        timestamp: Date.now(),
      }

      const state = get()

      // Log deferred warnings for debugging
      if (priority === 'high' && state.isSpeaking) {
        debug.log('[SpeechQueue] Warning queued - avatar currently speaking:', text.substring(0, 30) + '...')
      }

      // Add to queue and trigger processing
      state.queue.enqueue(item)
      get().processNext()

      return messageId
    },

    /**
     * Mark a speech item as successfully completed.
     * Clears its timeout, resolves any waiting promises, and triggers processing of next item.
     */
    markComplete: (messageId) => {
      const state = get()
      const pendingMessage = state.pendingMessages.get(messageId)

      if (pendingMessage) {
        clearTimeout(pendingMessage.timeoutId)
        state.pendingMessages.delete(messageId)
      }

      // Resolve any waiting promise for this message
      const resolver = speechPromiseResolvers.get(messageId)
      if (resolver) {
        resolver.resolve()
        speechPromiseResolvers.delete(messageId)
      }

      // Only update state if this was the current item
      if (state.currentItem?.messageId === messageId) {
        set({ currentItem: null, isSpeaking: false })
        get().processNext()
      }
    },

    /**
     * Mark a speech item as failed.
     * Retries if under limit, otherwise rejects any waiting promise and logs the failure.
     */
    markFailed: (messageId, reason) => {
      const state = get()
      const pendingMessage = state.pendingMessages.get(messageId)

      if (!pendingMessage) {
        debug.warn('[SpeechQueue] markFailed called for unknown messageId:', messageId)
        return
      }

      clearTimeout(pendingMessage.timeoutId)
      state.pendingMessages.delete(messageId)

      const { item } = pendingMessage

      // Retry if we haven't exceeded the limit
      if (item.retryCount < MAX_SPEECH_RETRIES) {
        debug.log(
          `[SpeechQueue] Retrying (attempt ${item.retryCount + 1}/${MAX_SPEECH_RETRIES}):`,
          item.text.substring(0, 30)
        )

        // Transfer the promise resolver to the new retry message ID
        const resolver = speechPromiseResolvers.get(messageId)
        if (resolver) {
          speechPromiseResolvers.delete(messageId)
        }

        const retryMessageId = generateMessageId()
        const retryItem: SpeechItem = {
          ...item,
          retryCount: item.retryCount + 1,
          messageId: retryMessageId,
        }

        // Re-attach resolver to new message ID
        if (resolver) {
          speechPromiseResolvers.set(retryMessageId, resolver)
        }

        get()._scheduleRetry(retryItem)
      } else {
        debug.error(`[SpeechQueue] Failed after ${MAX_SPEECH_RETRIES} retries:`, reason)

        // Reject any waiting promise after all retries exhausted
        const resolver = speechPromiseResolvers.get(messageId)
        if (resolver) {
          resolver.reject(reason)
          speechPromiseResolvers.delete(messageId)
        }
      }

      // Reset speaking state if this was the current item
      if (state.currentItem?.messageId === messageId) {
        set({ currentItem: null, isSpeaking: false })
        get().processNext()
      }
    },

    /**
     * Clear all queued items (current speech continues).
     * Useful when navigating away or resetting state.
     */
    clearQueue: () => {
      const state = get()

      // Clear all pending timeouts to prevent memory leaks
      state.pendingMessages.forEach(({ timeoutId }) => clearTimeout(timeoutId))

      // Reject all waiting promises for queued items
      state.queue.toArray().forEach((item) => {
        const resolver = speechPromiseResolvers.get(item.messageId)
        if (resolver) {
          resolver.reject('Queue cleared')
          speechPromiseResolvers.delete(item.messageId)
        }
      })

      state.queue.clear()
      state.pendingMessages.clear()

      debug.log('[SpeechQueue] Queue cleared')
    },

    /**
     * Process the next item in the queue.
     * Called automatically after enqueue or completion.
     *
     * Flow:
     * 1. Check if we can process (not speaking, has items, has speak function)
     * 2. Dequeue next item (priority queue handles ordering)
     * 3. Set up timeout for this item
     * 4. Call the speak function to send to avatar
     */
    processNext: () => {
      const state = get()

      // Guard: can't process if already speaking, queue empty, or no speak function
      if (state.isSpeaking || state.queue.length === 0 || !state.speakFn) {
        return
      }

      const nextItem = state.queue.dequeue()
      if (!nextItem) return

      // Set up timeout - if speech doesn't complete in time, mark as failed
      const timeoutId = setTimeout(() => {
        debug.warn('[SpeechQueue] Timeout for message:', nextItem.messageId)
        get().markFailed(nextItem.messageId, 'timeout')
      }, SPEAK_TIMEOUT_MS)

      // Track this pending message
      state.pendingMessages.set(nextItem.messageId, { item: nextItem, timeoutId })

      // Update state and trigger speech
      set({ currentItem: nextItem, isSpeaking: true })
      state.speakFn(nextItem)
    },

    /**
     * Schedule a retry after a delay.
     * Retries get high priority to complete quickly.
     */
    _scheduleRetry: (item) => {
      setTimeout(() => {
        const state = get()
        const retryItem = { ...item, priority: 'high' as SpeechPriority }
        state.queue.enqueue(retryItem)

        if (!state.isSpeaking) {
          get().processNext()
        }
      }, RETRY_DELAY_MS)
    },

    /**
     * Full cleanup - clears everything including current speech state.
     * Call when Avatar component unmounts.
     */
    _cleanup: () => {
      const state = get()

      state.pendingMessages.forEach(({ timeoutId }) => clearTimeout(timeoutId))
      state.queue.clear()
      state.pendingMessages.clear()

      set({
        currentItem: null,
        isSpeaking: false,
        speakFn: null,
      })

      debug.log('[SpeechQueue] Cleanup complete')
    },
  }))
)

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions for Non-React Code
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Make the avatar speak (callable from anywhere, not just React components).
 * @returns messageId for tracking
 */
export function avatarSpeak(text: string, priority: SpeechPriority = 'normal'): string {
  return useSpeechQueueStore.getState().enqueue(text, priority)
}

/** Clear all pending speech items */
export function avatarClearQueue(): void {
  useSpeechQueueStore.getState().clearQueue()
}

/** Check if avatar is currently speaking */
export function isAvatarSpeaking(): boolean {
  return useSpeechQueueStore.getState().isSpeaking
}

/** Get number of items waiting in queue */
export function getQueueLength(): number {
  return useSpeechQueueStore.getState().queue.length
}

/**
 * Make the avatar speak and wait for completion.
 * Returns a promise that resolves when speech finishes or rejects on failure.
 *
 * Use this when you need to wait for speech to complete before taking an action,
 * like transitioning to a new page.
 *
 * @example
 * await avatarSpeakAndWait("Welcome to the assessment!")
 * setStep('next-step') // Only runs after speech completes
 */
export function avatarSpeakAndWait(text: string, priority: SpeechPriority = 'normal'): Promise<void> {
  return new Promise((resolve, reject) => {
    const messageId = useSpeechQueueStore.getState().enqueue(text, priority)

    // Store the resolver so markComplete/markFailed can call it
    speechPromiseResolvers.set(messageId, { resolve, reject })
  })
}

/**
 * Wait for all currently queued speech items to complete.
 * Useful when you've queued multiple items and want to wait for all of them.
 */
export function waitForQueueEmpty(): Promise<void> {
  return new Promise((resolve) => {
    const checkQueue = () => {
      const state = useSpeechQueueStore.getState()
      if (!state.isSpeaking && state.queue.length === 0) {
        resolve()
      } else {
        // Check again in 100ms
        setTimeout(checkQueue, 100)
      }
    }
    checkQueue()
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Selectors for React Components
// ─────────────────────────────────────────────────────────────────────────────

/** Select speaking state (use with useSpeechQueueStore for fine-grained subscriptions) */
export const selectIsSpeaking = (state: SpeechQueueState): boolean => state.isSpeaking

/** Select current item being spoken */
export const selectCurrentItem = (state: SpeechQueueState): SpeechItem | null => state.currentItem

/** Select queue length */
export const selectQueueLength = (state: SpeechQueueState): number => state.queue.length

/** Select all queue items (for debugging) */
export const selectQueueItems = (state: SpeechQueueState): SpeechItem[] => state.queue.toArray()
